import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';

const FAQ_ITEMS = [
  {
    q: "How quickly will I receive a response?",
    a: "We typically respond to all email inquiries within 24 hours on business days. For urgent order issues, please include your order number in the subject line for priority handling."
  },
  {
    q: "When will my order ship?",
    a: "We aim to ship all orders the same or next business day. Most orders placed before 3:00 PM MST will ship the same business day if the product is in stock. Orders are not processed or shipped on Saturday or Sunday."
  },
  {
    q: "How can I track my order?",
    a: "Once your order has been packaged and a shipping label has been created, you'll receive an automated email containing your tracking information. You can also view tracking on your Dashboard under Orders."
  },
  {
    q: "Do you ship internationally?",
    a: "No. Shipping is currently limited to the United States only. We do not offer international shipping at this time."
  },
  {
    q: "What shipping options do you offer?",
    a: "Three flat-rate options at checkout: Standard Shipping (3-5 Business Days) for $9.99, 2 Day Shipping (2-3 Business Days) for $19.99, and Overnight Shipping (1-2 Business Days) for $59.99. Standard Shipping is FREE on orders of $100 or more, and 2 Day Shipping is FREE on orders of $175 or more."
  },
  {
    q: "Do you offer bulk pricing?",
    a: "Yes — bulk pricing is automatically applied to peptide vials per product. Buy 1-3 vials at standard price, 4-6 vials get 5% off per vial, 7-9 vials get 10% off per vial, and 10+ vials get 15% off per vial. Discounts are applied automatically in your cart."
  },
  {
    q: "I received the wrong item or my order is damaged. What should I do?",
    a: "Please email support@wilvada.com with your Order Number in the subject line within 48 hours of confirmed delivery. Include clear photos of the damaged product, the original shipping label, and the interior/exterior of the packaging. Claims are reviewed within 1-2 business days. See our Refunds & Returns page for full details."
  },
  {
    q: "Can I cancel or change my order?",
    a: "Orders can only be modified or canceled before fulfillment begins. To request a cancellation, open your order from the Dashboard and click 'Request Cancellation'. Once an order has been processed or shipped, it can no longer be canceled. We can not guarantee that a cancellation will be honored — see our Refunds & Returns page for full details."
  },
  {
    q: "How can I verify product quality?",
    a: "Every peptide product page has a Certificate of Analysis (COA) button — click it to view the latest batch-specific COA. We also display the COA inline on each product detail page. If you need additional documentation for a specific batch, contact support@wilvada.com."
  },
  {
    q: "What peptides do you currently carry?",
    a: "We carry GLP3-RT (10/20/30/60mg), GHK-Cu (50/100mg), Cagrilintide (5/10mg), AOD-9604 (2/5/10mg), and Bacteriostatic Water (10ml). Browse the full Catalog for current pricing and availability."
  },
  {
    q: "How should I store my research peptides?",
    a: "Lyophilized peptides should be stored at -20°C and protected from light and moisture. Reconstituted peptides should be stored at 2-8°C and used within 30 days. Bacteriostatic Water should be stored at room temperature and used within 28 days of first puncture. Specific storage instructions are listed on each product page."
  },
  {
    q: "What equipment do I need to reconstitute peptides?",
    a: "Bacteriostatic Water as the diluent, syringes (we carry 1ml and 3ml options), and alcohol prep pads for sterilization. We also offer sharps disposal containers and insulated peptide storage cases. All available in our Equipment section."
  },
  {
    q: "Are your products safe for human or veterinary use?",
    a: "No. All products sold by Wilvada LC are strictly For Research Use Only (RUO). They are not intended for human or veterinary consumption, diagnostic, therapeutic, or agricultural purposes. By purchasing, you confirm the product will be used exclusively for in-vitro laboratory research."
  },
  {
    q: "How do I create an account or sign in?",
    a: "Click the Sign In link in the header. New customers can register with email + password and choose their Account Type (Independent Researcher / Business / Institution) and Industry. Use the 'Remember Me' checkbox to stay signed in for 90 days across visits."
  },
  {
    q: "Do you have a rewards program?",
    a: "Yes — every customer earns 1 reward point per dollar spent. Points are visible in your Dashboard under Points & Rewards and can be redeemed for future discounts."
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards through our secure Stripe checkout. Each order is tagged with its payment method (Credit/Debit Card or Complimentary if a 100% promo was applied). Crypto payments are coming soon."
  },
  {
    q: "My payment was declined. What should I do?",
    a: "Ensure your billing address matches your card exactly. If issues persist, try a different payment method or contact your bank to authorize the transaction. You can also email support@wilvada.com and we'll help troubleshoot."
  }
];

export default function FAQPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="faq-page">
      <SEO title="Frequently Asked Questions" description="Answers to common questions about ordering Wilvada research peptides — shipping (flat-rate options + free thresholds), bulk pricing tiers, COA verification, storage, payment, and account management." />
      <div className="text-center mb-12">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,30%)] mb-2 font-body">Support</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-foreground font-heading mb-4">Frequently Asked Questions</h1>
        <p className="text-base text-muted-foreground font-body">Quick answers to common questions about our products and services.</p>
      </div>

      <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-lg px-6 bg-card" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-heading font-medium text-sm sm:text-base text-foreground hover:no-underline py-5">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground font-body leading-relaxed pb-5">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-12 text-center p-8 rounded-lg bg-muted/50 border border-border">
        <h3 className="font-heading font-semibold text-foreground mb-2">Still have questions?</h3>
        <p className="text-sm text-muted-foreground font-body mb-4">
          Can't find what you're looking for? Our support team is ready to help.
        </p>
        <a href="mailto:support@wilvada.com" className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(210,45%,30%)] hover:text-[hsl(210,45%,30%)] transition-colors">
          Email Support
        </a>
      </div>
    </div>
  );
}
